import { Loader } from '@crossfit/ui'
import { useAuth } from 'components/Auth'
import {
  canAccessProgramming,
  canAccessPlaybook,
  canAccessDashboard,
  canAccessPartnerNetwork,
  canAccessRoundtables,
  canAccessAffiliateStarterKit,
  canAccessAdmin,
  canAccessContentKit,
  canAccessAdminDirectoryPage,
  canAccessAdminContactInfoPage,
  canAccessAdminBillingAndInvoices,
  canAccessAdminCoachesAndStaffPage,
  canAccessAdminHelpPage,
  canAccessAdminContactUsPage,
  canAccessPricing,
  canAccessTeammates,
  canInvite,
} from 'components/Auth/abilities'
import { useMyActiveSubscriptions } from 'hooks/subscriptions'
import { createContext, useEffect, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import {
  fetchAffiliate,
  fetchInvitations,
  fetchMembers,
  fetchMyAffiliates,
} from 'services/affiliates'
import {
  AffiliateMember,
  CombinedAffiliate,
  Invitation,
  MyAffiliate,
  Role,
} from 'types'
import * as agreementsService from 'services/agreements'
import { useLocation } from 'react-router-dom'

export type AppContext = {
  myAffiliates: MyAffiliate[]
  selectedAffiliate: CombinedAffiliate
  affiliateCoachOrdering: AffiliateMember[]
  hasValidAgreement: boolean
  invitations: Invitation[]
  canAccessProgramming: boolean
  canAccessPlaybook: boolean
  canAccessDashboard: boolean
  canAccessTeammates: boolean
  canAccessContentKit: boolean
  canAccessPartnerNetwork: boolean
  canAccessRoundtables: boolean
  canAccessStarterKit: boolean
  canAccessAdmin: boolean
  canAccessAdminDirectoryPage: boolean
  canAccessAdminContactInfoPage: boolean
  canAccessAdminBillingAndInvoices: boolean
  canAccessAdminCoachesAndStaffPage: boolean
  canAccessAdminHelpPage: boolean
  canAccessAdminContactUsPage: boolean
  canAccessPricing: boolean
  canInvite: boolean
  isCoach: boolean
  isLoading: boolean
  setAffiliateCoachOrdering
  setSelectedAffiliate: (affiliateId: number) => void
}

export const Context = createContext({} as AppContext)

export function AppProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const queryClient = useQueryClient()
  const { user, isLoading } = useAuth()
  const [selectedAffiliate, setAffiliate] = useState(null)
  const myAffiliatesQuery = useQuery(
    'affiliate',
    () => fetchMyAffiliates(user.id),
    {
      enabled: !!user?.id,
    },
  )
  const affiliateDetailQuery = useQuery(
    ['selectedAffiliateDetail', selectedAffiliate?.affiliateId],
    () => fetchAffiliate(String(selectedAffiliate?.affiliateId)),
    { enabled: !!selectedAffiliate?.affiliateId },
  )
  const affiliateMembersQuery = useQuery(
    ['selectedAffiliateMembers', selectedAffiliate?.affiliateId],
    () => fetchMembers(selectedAffiliate?.affiliateId),
    { enabled: !!selectedAffiliate?.affiliateId },
  )
  const agreementQuery = useQuery(
    'agreement',
    () => agreementsService.fetchUserAgreement(user.id),
    {
      enabled: !!user?.id,
    },
  )
  const invitationsQuery = useQuery(
    ['invitations', (selectedAffiliate as MyAffiliate)?.affiliateId],
    () => fetchInvitations((selectedAffiliate as MyAffiliate)?.affiliateId),
    { enabled: !!(selectedAffiliate as MyAffiliate)?.affiliateId },
  )
  const agreement = agreementQuery.data
  const hasValidAgreement = agreement?.valid === true
  const [affiliateCoachOrdering, setAffiliateCoachOrdering] = useState([])
  const combinedAffiliate: CombinedAffiliate = {
    ...selectedAffiliate,
    ...affiliateDetailQuery.data,
    members: affiliateMembersQuery.data,
  }
  const activeSubscriptions = useMyActiveSubscriptions()
  const programmingEligible = canAccessProgramming(
    combinedAffiliate,
    activeSubscriptions,
  )
  const playbookEligible = canAccessPlaybook(
    combinedAffiliate,
    activeSubscriptions,
  )
  const dashboardEligible = canAccessDashboard(
    combinedAffiliate,
    activeSubscriptions,
  )

  const teammatesEligible = canAccessTeammates(
    combinedAffiliate,
    activeSubscriptions,
  )

  const partnerNetworkEligible = canAccessPartnerNetwork(
    combinedAffiliate,
    activeSubscriptions,
  )
  const roundtablesEligible = canAccessRoundtables(
    combinedAffiliate,
    activeSubscriptions,
  )
  const starterKitEligible = canAccessAffiliateStarterKit(
    combinedAffiliate,
    activeSubscriptions,
  )
  const adminEligible = canAccessAdmin(combinedAffiliate, activeSubscriptions)
  const contentKitEligible = canAccessContentKit(
    combinedAffiliate,
    activeSubscriptions,
  )
  const adminDirectoryPageEligible = canAccessAdminDirectoryPage(
    combinedAffiliate,
    activeSubscriptions,
  )
  const adminContactInfoPageEligible = canAccessAdminContactInfoPage(
    combinedAffiliate,
    activeSubscriptions,
  )
  const adminBillingAndInvoicesPageEligible = canAccessAdminBillingAndInvoices(
    combinedAffiliate,
    activeSubscriptions,
  )
  const adminCoachesAndStaffPageEligible = canAccessAdminCoachesAndStaffPage(
    combinedAffiliate,
    activeSubscriptions,
  )
  const adminHelpPageEligible = canAccessAdminHelpPage(
    combinedAffiliate,
    activeSubscriptions,
  )
  const adminContactUsPageEligible = canAccessAdminContactUsPage(
    combinedAffiliate,
    activeSubscriptions,
  )
  const pricingEligible = canAccessPricing(
    combinedAffiliate,
    activeSubscriptions,
  )
  const inviteEligible = canInvite(combinedAffiliate, activeSubscriptions)
  const setSelectedAffiliate = (affiliateId: number) => {
    queryClient.invalidateQueries({
      queryKey: ['selectedAffiliateDetail', affiliateId],
    })
    queryClient.invalidateQueries({
      queryKey: ['selectedAffiliateMembers', affiliateId],
    })

    const newAffiliate = myAffiliatesQuery?.data?.find(
      (a) => a.affiliateId === affiliateId,
    )

    if (newAffiliate) {
      setAffiliate(newAffiliate)
    }
  }

  useEffect(() => {
    if (myAffiliatesQuery.isSuccess && myAffiliatesQuery.data?.length > 0) {
      setAffiliate(myAffiliatesQuery.data[0])
    }
  }, [myAffiliatesQuery?.data])

  useEffect(() => {
    if (!location.pathname.includes('redirect')) {
      window.needsToTrackScrollDepth = true
      analytics.page({
        affiliateId: selectedAffiliate?.affiliateId,
        user_role:
          selectedAffiliate?.roles?.length > 0
            ? selectedAffiliate?.roles[0]
            : null,
        group_id: selectedAffiliate?.groupId,
      })
    }
  }, [location])

  if (
    isLoading ||
    myAffiliatesQuery.isLoading ||
    affiliateDetailQuery.isLoading ||
    affiliateMembersQuery.isLoading ||
    agreementQuery.isLoading ||
    invitationsQuery.isLoading
  ) {
    return <Loader />
  }

  return (
    <Context.Provider
      value={{
        myAffiliates: myAffiliatesQuery.data,
        selectedAffiliate: combinedAffiliate,
        affiliateCoachOrdering,
        hasValidAgreement,
        invitations: invitationsQuery.data,
        canAccessDashboard: dashboardEligible,
        canAccessTeammates: teammatesEligible,
        canAccessProgramming: programmingEligible,
        canAccessPlaybook: playbookEligible,
        canAccessPartnerNetwork: partnerNetworkEligible,
        canAccessRoundtables: roundtablesEligible,
        canAccessContentKit: contentKitEligible,
        canAccessStarterKit: starterKitEligible,
        canAccessAdmin: adminEligible,
        canAccessAdminCoachesAndStaffPage: adminCoachesAndStaffPageEligible,
        canAccessAdminDirectoryPage: adminDirectoryPageEligible,
        canAccessAdminContactInfoPage: adminContactInfoPageEligible,
        canAccessAdminContactUsPage: adminContactUsPageEligible,
        canAccessAdminBillingAndInvoices: adminBillingAndInvoicesPageEligible,
        canAccessAdminHelpPage: adminHelpPageEligible,
        canAccessPricing: pricingEligible,
        isCoach: combinedAffiliate?.roles?.includes(Role.Coach),
        canInvite: inviteEligible,
        isLoading:
          myAffiliatesQuery.isLoading ||
          affiliateDetailQuery.isLoading ||
          affiliateMembersQuery.isLoading,
        setAffiliateCoachOrdering,
        setSelectedAffiliate,
      }}
    >
      {children}
    </Context.Provider>
  )
}
